import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { currencyFormatter } from '../../../utils/currencyFormat';
import CustomPagination from '../../../components/customPagination';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { usePdfActions } from '../../common/DocumentPdf';
import { formatDate } from '../../../utils/formatDate';
import { useNavigate } from 'react-router-dom';
import { useGetProformaInvoicesHandler } from '../dataHandlers/getProformaInvoicesHandler';
import ProformaInvoiceStatusChip from './ProformaInvoiceStatusChip';
import EditIcon from '@mui/icons-material/Edit';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { CREATE_SALE_INVOICE, UPDATE_PROFORMA_INVOICE } from '../../../constants/eInvoicePermissions';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';

const ProformaInvoiceTable = ({ handlePaginationChange, searchQuery = {} }) => {
  const { downloadPdf, handlePrintPdf } = usePdfActions();
  const navigate = useNavigate();
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const canCreateEInvoice = hasEInvoicePermission(CREATE_SALE_INVOICE);
  const canEditProforma = hasEInvoicePermission(UPDATE_PROFORMA_INVOICE);
  const { invoices, totalDocuments, isLoading } = useGetProformaInvoicesHandler({ searchQuery });
  const { proformaInvoices: pi } = useTranslatedMessages();
  const cols = pi?.table?.columns ?? {};
  const tips = pi?.table?.tooltips ?? {};

  const handleCreateEInvoice = (row) => {
    if (!canCreateEInvoice) {
      return;
    }
    navigate(`/sales/documents/invoices/create`, { state: { document: row } });
  };

  const handleEditProforma = (id) => {
    if (!canEditProforma) {
      return;
    }
    navigate(`/sales/proforma-invoices/${id}/edit`);
  };

  const iconButtonSx = {
    '&.Mui-disabled': {
      opacity: 0.3,
    },
  };

  const tooltipSlotProps = {
    tooltip: { sx: { maxWidth: 300 } },
  };

  const columns = [
    {
      headerName: cols?.status ?? 'Status',
      field: 'status',
      flex: 0.5,
      renderCell: (params) => {
        return <ProformaInvoiceStatusChip status={params.row.status} />;
      },
    },
    {
      headerName: cols?.proforma_invoice_no ?? 'Proforma Invoice No',
      field: 'document_number',
      flex: 1,
      renderCell: (params) => (
        <Typography
          component="span"
          variant="span"
          onClick={() => navigate(`/sales/proforma-invoices/${params.row.id}`)}
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
            '&:focus': {
              outline: 'none',
            },
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      headerName: cols?.source ?? 'Source',
      field: 'document_source',
      flex: 1,
      valueGetter: (params) => {
        return params;
      },
    },
    {
      headerName: cols?.customer_info ?? 'Customer Info',
      field: 'accounting_customer_party',
      flex: 1,
      valueGetter: (params) => {
        if (!params) return 'N/A';
        return params.name;
      },
    },
    {
      headerName: cols?.invoice_date ?? 'Invoice Date',
      field: 'issue_date',
      flex: 1,
      valueGetter: (params) => {
        return formatDate(params);
      },
    },
    {
      headerName: cols?.amount ?? 'Amount',
      field: 'payable_amount',
      flex: 1,
      renderCell: ({ row }) =>
        row.document_currency_code === 'AED'
          ? `AED ${currencyFormatter().format(row.payable_amount)}`
          : currencyFormatter(row.document_currency_code).format(row.payable_amount),
    },
    {
      field: 'actions',
      headerName: cols?.actions ?? 'Actions',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              gap: 0.6,
            }}
          >
            {params?.row?.status === 'CREATED' ? (
              <>
                <Tooltip title={tips?.download ?? 'Download Document'}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      downloadPdf({
                        url: `proforma-invoices/${params.row.id}/download-pdf`,
                        fileName: `PINV-${params.row.id}.pdf`,
                      })
                    }
                    data-testid="download-pdf-button"
                    sx={iconButtonSx}
                  >
                    <PictureAsPdfIcon fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={tips?.print ?? 'Print Document'}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      handlePrintPdf({
                        url: `proforma-invoices/${params.row.id}/download-pdf`,
                        fileName: `PINV-${params.row.id}.pdf`,
                      })
                    }
                    data-testid="print-pdf-button"
                    sx={iconButtonSx}
                  >
                    <PrintIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={canCreateEInvoice ? (tips?.create_e_invoice ?? 'Create E Invoice') : accessDeniedMessage}
                  arrow
                  slotProps={tooltipSlotProps}
                >
                  <span style={{ display: 'inline-flex' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleCreateEInvoice(params.row)}
                      data-testid="create-e-invoice-button"
                      disabled={!canCreateEInvoice}
                      sx={iconButtonSx}
                    >
                      <FileOpenIcon fontSize="small" color="primary" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : (
              <Tooltip
                title={canEditProforma ? (tips?.edit ?? 'Edit Document') : accessDeniedMessage}
                arrow
                slotProps={tooltipSlotProps}
              >
                <span style={{ display: 'inline-flex' }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditProforma(params.row.id)}
                    data-testid="edit-pdf-button"
                    disabled={!canEditProforma}
                    sx={iconButtonSx}
                  >
                    <EditIcon fontSize="small" color="primary" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={invoices}
        columns={columns}
        getRowId={(row) => row.id}
        paginationMode="server"
        rowCount={totalDocuments}
        loading={isLoading}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />
    </Box>
  );
};

export default ProformaInvoiceTable;
